// Radar minimap: player-centred view of foes, drops and the arena edge. Pure 2D canvas, reads Sim state only.
import { ARENA_R, Sim } from '../core/sim';
import { hexCss, tierColor, xpTier } from '../render/gemTier';

export const RADAR_RANGE = 56; const RANGE = RADAR_RANGE; // world units from centre to edge of the radar (48 → 56 with the 160 m arena, owner 2026-11-02)
/** PICKUP-REFORGE: Salve as a crimson cross, Sweep as a ring with inward ticks (?pickups=classic keeps the old dots) */
const RELICS = typeof location === 'undefined' || new URLSearchParams(location.search).get('pickups') !== 'classic';

export class Minimap {
  private ctx: CanvasRenderingContext2D;
  private size = 0;
  private acc = 0;

  /** CSS size of the radar, kept by a ResizeObserver: the fluid layout (src/style.css) only changes it on resize or
   *  rotation, so the 20 Hz redraw never forces a layout read. */
  private cssSize = 150;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
    this.cssSize = canvas.clientWidth || 150;
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver((es) => { const w = es[es.length - 1].contentRect.width; if (w > 0) this.cssSize = w; }).observe(canvas);
  }

  private fit(): void {
    const css = this.cssSize;
    const px = Math.round(css * Math.min(2, window.devicePixelRatio || 1));
    if (px !== this.size) { this.size = px; this.canvas.width = px; this.canvas.height = px; }
  }

  /** Redraw at ~20 Hz to keep DOM/CPU cost negligible. */
  update(sim: Sim, dt: number, force = false): void {
    this.acc += dt;
    if (!force && this.acc < 0.05) return;
    this.acc = 0;
    this.fit();
    const g = this.ctx, S = this.size, R = S / 2, k = R / RANGE;
    const p = sim.player;
    g.clearRect(0, 0, S, S);
    g.save();
    g.beginPath(); g.arc(R, R, R - 1, 0, Math.PI * 2); g.clip();
    g.fillStyle = 'rgba(8,12,30,0.72)'; g.fillRect(0, 0, S, S);
    // range rings
    g.strokeStyle = 'rgba(120,200,255,0.18)'; g.lineWidth = Math.max(1, S / 150);
    for (const rr of [0.33, 0.66]) { g.beginPath(); g.arc(R, R, R * rr, 0, Math.PI * 2); g.stroke(); }
    // arena barrier
    g.strokeStyle = 'rgba(90,180,255,0.85)'; g.lineWidth = Math.max(1.5, S / 75);
    g.beginPath(); g.arc(R - p.x * k, R - p.z * k, ARENA_R * k, 0, Math.PI * 2); g.stroke();

    const dot = (x: number, z: number, r: number, color: string) => {
      const sx = R + (x - p.x) * k, sz = R + (z - p.z) * k;
      if (sx < -r || sz < -r || sx > S + r || sz > S + r) return;
      g.fillStyle = color; g.beginPath(); g.arc(sx, sz, r, 0, Math.PI * 2); g.fill();
    };
    const u = S / 150; // scale factor relative to a 150px radar
    // solid terrain (rock spires + boulders) so routes are readable
    const near2 = (RANGE + 8) ** 2;
    for (const pr of sim.terrain.props) {
      if (!pr.alive || !pr.solid || (pr.x - p.x) ** 2 + (pr.z - p.z) ** 2 > near2) continue;
      dot(pr.x, pr.z, Math.max(1.2 * u, pr.r * k), pr.kind === 'spire' ? 'rgba(214,170,120,0.75)' : 'rgba(170,150,128,0.6)');
    }
    // drops
    // XP by value (src/render/gemTier.ts): the same palette as the world gems (colour-blind modes included). Hue, size and shape all step up:
    // Small / Medium dots, Large a diamond, Huge a bigger diamond with a gold core. Bigger ones get a dark rim so they hold on the dim radar.
    const tiers = [hexCss(tierColor(0)), hexCss(tierColor(1)), hexCss(tierColor(2)), hexCss(tierColor(3))];
    const diamond = (x: number, z: number, r: number, color: string) => {
      const sx = R + (x - p.x) * k, sz = R + (z - p.z) * k;
      if (sx < -r || sz < -r || sx > S + r || sz > S + r) return;
      g.fillStyle = color; g.beginPath(); g.moveTo(sx, sz - r); g.lineTo(sx + r, sz); g.lineTo(sx, sz + r); g.lineTo(sx - r, sz); g.closePath(); g.fill();
    };
    for (let pass = 0; pass < 2; pass++) for (const gem of sim.gems) { // pass 0: small / medium (under), pass 1: large / huge (over)
      if (!gem.alive || gem.kind >= 4 || (gem.kind === 3 && RELICS)) continue; // PICKUP-REFORGE: Salve drawn as a cross below
      if (gem.kind === 3) { dot(gem.x, gem.z, 2.6 * u, '#5dff8a'); continue; }
      const t = xpTier(gem.kind, gem.value);
      if ((t >= 2 ? 1 : 0) !== pass) continue;
      if (t < 2) { if (t === 1) dot(gem.x, gem.z, 2.3 * u, 'rgba(8,12,30,0.9)'); dot(gem.x, gem.z, (t === 1 ? 1.6 : 1.2) * u, tiers[t]); continue; }
      diamond(gem.x, gem.z, (t === 3 ? 4.6 : 3.4) * u, 'rgba(8,12,30,0.92)');
      diamond(gem.x, gem.z, (t === 3 ? 3.5 : 2.5) * u, tiers[t]);
      if (t === 3) dot(gem.x, gem.z, 1.2 * u, '#ffd24a');
    }
    // PICKUP-REFORGE Vital Salve: crimson cross with a dark rim, above the XP dots
    if (RELICS) for (const gem of sim.gems) {
      if (!gem.alive || gem.kind !== 3) continue;
      const sx = R + (gem.x - p.x) * k, sz = R + (gem.z - p.z) * k, a = 3 * u, b = 1.1 * u;
      if (sx < -a || sz < -a || sx > S + a || sz > S + a) continue;
      g.fillStyle = '#2a0a12'; g.fillRect(sx - a - u, sz - b - u, 2 * (a + u), 2 * (b + u)); g.fillRect(sx - b - u, sz - a - u, 2 * (b + u), 2 * (a + u));
      g.fillStyle = '#ff5a72'; g.fillRect(sx - a, sz - b, 2 * a, 2 * b); g.fillRect(sx - b, sz - a, 2 * b, 2 * a);
    }
    // special drops: seals (gold diamond) and sweep beacons (cyan)
    for (const gem of sim.gems) {
      if (!gem.alive || gem.kind < 4) continue;
      const sx = R + (gem.x - p.x) * k, sz = R + (gem.z - p.z) * k, s = 3.4 * u;
      if (gem.kind === 5 && RELICS) { // PICKUP-REFORGE Sweep Compass: cyan ring with four inward ticks
        g.strokeStyle = '#60f0ff'; g.lineWidth = 1.6 * u; g.beginPath(); g.arc(sx, sz, s * 0.85, 0, Math.PI * 2);
        for (let q = 0; q < 4; q++) { const a = q * Math.PI / 2; g.moveTo(sx + Math.cos(a) * s * 1.35, sz + Math.sin(a) * s * 1.35); g.lineTo(sx + Math.cos(a) * s * 0.35, sz + Math.sin(a) * s * 0.35); }
        g.stroke(); continue;
      }
      g.fillStyle = gem.kind === 4 ? '#ffd860' : '#60f0ff';
      g.beginPath(); g.moveTo(sx, sz - s); g.lineTo(sx + s, sz); g.lineTo(sx, sz + s); g.lineTo(sx - s, sz); g.closePath(); g.fill();
    }
    // foes
    for (const e of sim.enemies) {
      if (!e.alive || (e.def.kind === 'boss' && !e.dummy) || e.mode < 0) continue; // (MAP FOES: an underground foe isn't on the radar)
      const c = e.dummy ? '#7ff4ff' : e.elite ? '#ffd040' : e.def.kind === 'caster' ? '#c070ff' : e.def.kind === 'brute' ? '#ff7040' : '#ff3a4a';
      dot(e.x, e.z, (e.elite ? 3.2 : e.def.kind === 'brute' ? 2.4 : 1.7) * u, c);
    }
    g.restore();

    // boss: drawn on top, clamped to the rim with a pointer when out of range
    if (sim.boss && sim.boss.alive) {
      let dx = (sim.boss.x - p.x) * k, dz = (sim.boss.z - p.z) * k;
      const d = Math.hypot(dx, dz), lim = R - 7 * u;
      const clamped = d > lim;
      if (clamped) { dx *= lim / d; dz *= lim / d; }
      g.fillStyle = '#ff40c0'; g.strokeStyle = '#fff'; g.lineWidth = 1.5 * u;
      g.beginPath();
      if (clamped) {
        const a = Math.atan2(dz, dx);
        g.moveTo(R + dx + Math.cos(a) * 6 * u, R + dz + Math.sin(a) * 6 * u);
        g.lineTo(R + dx + Math.cos(a + 2.4) * 5 * u, R + dz + Math.sin(a + 2.4) * 5 * u);
        g.lineTo(R + dx + Math.cos(a - 2.4) * 5 * u, R + dz + Math.sin(a - 2.4) * 5 * u);
        g.closePath();
      } else g.arc(R + dx, R + dz, 5 * u, 0, Math.PI * 2);
      g.fill(); g.stroke();
    }
    // off-radar seal pointers on the rim
    for (const gem of sim.gems) {
      if (!gem.alive || gem.kind !== 4) continue;
      const dx = gem.x - p.x, dz = gem.z - p.z, d = Math.hypot(dx, dz);
      if (d * k < R - 6 * u) continue;
      const a = Math.atan2(dz, dx), rr = R - 6 * u;
      g.fillStyle = '#ffd860'; g.beginPath();
      g.moveTo(R + Math.cos(a) * (rr + 4 * u), R + Math.sin(a) * (rr + 4 * u));
      g.lineTo(R + Math.cos(a + 0.12) * (rr - 3 * u), R + Math.sin(a + 0.12) * (rr - 3 * u));
      g.lineTo(R + Math.cos(a - 0.12) * (rr - 3 * u), R + Math.sin(a - 0.12) * (rr - 3 * u));
      g.closePath(); g.fill();
    }
    // MAP PICKUPS (owner 2026-11-02): relic caches (violet-gold hexagon) and chests (gold box; boss chests larger with a white
    // rim). On the radar when in range, else a small marker on the rim pointing the way.
    const L = sim.loot;
    for (const c of L.caches) if (c.state !== 2) this.lootIcon(c.x, c.z, 'cache', c.state === 1, R, k, u, p.x, p.z);
    for (const c of L.chests) if (c.alive) this.lootIcon(c.x, c.z, c.boss ? 'boss' : 'chest', false, R, k, u, p.x, p.z);
    // player (arrow showing facing)
    const a = Math.atan2(p.faceZ, p.faceX);
    g.fillStyle = sim.transformed ? '#ffe070' : '#ffffff';
    g.beginPath();
    g.moveTo(R + Math.cos(a) * 6 * u, R + Math.sin(a) * 6 * u);
    g.lineTo(R + Math.cos(a + 2.5) * 4.5 * u, R + Math.sin(a + 2.5) * 4.5 * u);
    g.lineTo(R + Math.cos(a - 2.5) * 4.5 * u, R + Math.sin(a - 2.5) * 4.5 * u);
    g.closePath(); g.fill();
    // rim
    g.strokeStyle = 'rgba(140,210,255,0.7)'; g.lineWidth = 2 * u;
    g.beginPath(); g.arc(R, R, R - 1, 0, Math.PI * 2); g.stroke();
  }

  /** a cache / chest glyph at its radar spot, or a rim marker (same colours) when it is out of range */
  private lootIcon(x: number, z: number, kind: 'cache' | 'chest' | 'boss', opening: boolean, R: number, k: number, u: number, px: number, pz: number): void {
    const g = this.ctx;
    let dx = (x - px) * k, dz = (z - pz) * k;
    const d = Math.hypot(dx, dz), lim = R - 7 * u, out = d > lim;
    const fill = kind === 'cache' ? (opening ? '#fff2b0' : '#c9a2ff') : '#ffcc4a', rim = kind === 'boss' ? '#ffffff' : '#2a1a06';
    if (out) {
      dx *= lim / d; dz *= lim / d;
      const a = Math.atan2(dz, dx), cx = R + dx, cz = R + dz, s = (kind === 'boss' ? 5 : 4) * u;
      g.fillStyle = fill; g.strokeStyle = rim; g.lineWidth = 1.2 * u; g.beginPath();
      g.moveTo(cx + Math.cos(a) * s, cz + Math.sin(a) * s);
      g.lineTo(cx + Math.cos(a + 2.3) * s * 0.8, cz + Math.sin(a + 2.3) * s * 0.8);
      g.lineTo(cx + Math.cos(a - 2.3) * s * 0.8, cz + Math.sin(a - 2.3) * s * 0.8);
      g.closePath(); g.fill(); g.stroke();
      return;
    }
    const cx = R + dx, cz = R + dz;
    g.lineWidth = 1.4 * u; g.strokeStyle = rim; g.fillStyle = fill;
    if (kind === 'cache') {
      const s = 4.2 * u; g.beginPath();
      for (let q = 0; q < 6; q++) { const a = q * Math.PI / 3 + Math.PI / 6; if (q) g.lineTo(cx + Math.cos(a) * s, cz + Math.sin(a) * s); else g.moveTo(cx + Math.cos(a) * s, cz + Math.sin(a) * s); }
      g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#ffe48a'; g.beginPath(); g.arc(cx, cz, 1.4 * u, 0, Math.PI * 2); g.fill();
    } else {
      const w = (kind === 'boss' ? 5.2 : 4) * u, h = w * 0.72;
      g.fillRect(cx - w, cz - h, 2 * w, 2 * h); g.strokeRect(cx - w, cz - h, 2 * w, 2 * h);
      g.strokeStyle = '#7a4a10'; g.beginPath(); g.moveTo(cx - w, cz - h * 0.2); g.lineTo(cx + w, cz - h * 0.2); g.stroke();
    }
  }
}
